import { useEntityIndex } from '../../hooks/useEntityIndex';
import { enumLabel, humanize } from '../../lib/enums';
import { formatMoneyCompact } from '../../lib/format';

/**
 * The vocabulary the role screen and the person screen both speak.
 *
 * Everything a capability says about itself — its wording, whether it takes a money cap, what its
 * scope ids point at — comes off the API and is never restated here. The portal used to be the
 * kind of place where a list like that gets typed out a second time; the copy then disagrees with
 * the register, and the register is the one people believe. What lives in this file is only the arranging:
 * which order to read the surfaces in, and how to turn one grant into words a manager would say.
 */

export type PermissionMode = 'perform' | 'approve';

/** One row of the catalog: something a person can be allowed to do, and how. */
export interface Permission {
  id: string;
  surface: string;
  action: string;
  mode: PermissionMode;
  scopeKind: string | null;
  takesLimit: boolean;
  description: string | null;
}

/** One capability actually given to a role, with the cap and the places it applies. */
export interface RoleGrant {
  id: string;
  roleId: string;
  permissionId: string;
  scopeIds: string[];
  limitMinor: number | null;
}

/** One capability a person ends up with, after every role they hold has been merged. */
export interface StaffCapability {
  surface: string;
  action: string;
  mode: PermissionMode;
  scopeKind: string | null;
  scopeIds: string[];
  limitMinor: number | null;
  description: string | null;
}

export interface StaffCapabilities {
  staffId: string;
  displayName: string;
  locationId: string;
  status: string;
  roles: { roleId: string; name: string }[];
  capabilities: StaffCapability[];
  resolvedAt: string;
}

export const PERMISSIONS_PATH = '/admin/v1/staff/permissions';
export const ROLES_PATH = '/admin/v1/staff/roles';
export const rolePermissionsPath = (roleId: string) => `${ROLES_PATH}/${roleId}/permissions`;
export const roleMembersPath = (roleId: string) => `${ROLES_PATH}/${roleId}/members`;
export const memberRolesPath = (staffId: string) => `/admin/v1/staff/members/${staffId}/roles`;
export const memberCapabilitiesPath = (staffId: string) => `/admin/v1/staff/members/${staffId}/capabilities`;

/**
 * Reading order for the places a role reaches, which is not alphabetical and not the order the API
 * happens to return. A server's whole night is the register, so the register comes first; the figures they
 * are allowed to look at afterwards come last. Anything the API adds later that is not on this
 * list still shows, at the end, rather than disappearing.
 */
const SURFACE_ORDER = ['pos', 'drawer', 'kds', 'inventory', 'timeclock', 'reports'];

export function surfaceRank(surface: string): number {
  const at = SURFACE_ORDER.indexOf(surface);
  return at === -1 ? SURFACE_ORDER.length : at;
}

/**
 * `portal` exists in the API's surface enum and must never carry a grant: staff do not have
 * accounts on this site, so a permission claiming otherwise is a promise no sign-in keeps. The
 * backend holds that line with a test; the portal simply refuses to draw a checkbox for it, so a
 * row that ever did appear cannot be ticked by somebody who assumed it meant something.
 */
export function onlyStaffSurfaces<T extends { surface: string }>(rows: readonly T[]): T[] {
  return rows.filter((r) => r.surface !== 'portal');
}

/** Groups rows by surface, in reading order, keeping each surface's own order intact. */
export function bySurface<T extends { surface: string }>(rows: readonly T[]): [string, T[]][] {
  const groups = new Map<string, T[]>();
  for (const row of rows) {
    const existing = groups.get(row.surface);
    if (existing) existing.push(row);
    else groups.set(row.surface, [row]);
  }
  return [...groups.entries()].sort((a, b) => surfaceRank(a[0]) - surfaceRank(b[0]));
}

/**
 * Resolves the ids inside a grant's scope to the names of the actual places.
 *
 * Two different kinds of thing hide behind `scopeIds`. A storage area is a row someone created and
 * can rename, so it has to be looked up; a kitchen station is a fixed part of the menu, so it is
 * read from the shared enum wording instead. Either way what reaches the screen is "Bar", never
 * the identifier — the same rule every other reference in this portal follows.
 */
export function useScopeNames(): (scopeKind: string | null, scopeIds: readonly string[]) => string[] {
  const areas = useEntityIndex('storageLocation');
  return (scopeKind, scopeIds) =>
    scopeIds.map((id) => {
      if (scopeKind === 'storageLocation') return areas.label(id);
      if (scopeKind === 'kdsStation') return enumLabel('prepStation', id);
      return humanize(id);
    });
}

/**
 * "Take payment" → "take payment", so a capability can be dropped into the middle of a sentence.
 *
 * A first word that is entirely capitals is left alone: the catalog talks about CAI numbers, and
 * "issue an invoice with a cai number" is a different word from the one printed on the paperwork.
 */
function lowerFirst(text: string): string {
  const [first] = text.split(' ');
  if (!first || (first.length > 1 && first === first.toUpperCase())) return text;
  return text.charAt(0).toLowerCase() + text.slice(1);
}

/**
 * One capability in words: "Apply a discount to a tab up to L 500 (Bar only)".
 *
 * The wording is the API's own, so the portal and the register describe the same permission with the
 * same words. The cap and the places are appended because they are the difference between a shift
 * lead and a manager, and leaving them off would make two very different roles read identically.
 */
export function capabilityPhrase(
  capability: Pick<StaffCapability, 'description' | 'action' | 'limitMinor'>,
  scopeNames: readonly string[],
  currency: string,
): string {
  const base = capability.description ?? humanize(capability.action);
  const cap =
    typeof capability.limitMinor === 'number'
      ? ` up to ${formatMoneyCompact(capability.limitMinor, currency)}`
      : '';
  const where = scopeNames.length > 0 ? ` (${scopeNames.join(', ')} only)` : '';
  return `${base}${cap}${where}`;
}

/**
 * The same capability with a small first letter, for the middle of a sentence.
 *
 * A list can carry the API's own sentence case; "…can Take payment, Apply a discount" cannot, and
 * lowercasing the whole list to match the sentence would leave every line looking half-typed.
 */
export function capabilityFragment(
  capability: Pick<StaffCapability, 'description' | 'action' | 'limitMinor'>,
  scopeNames: readonly string[],
  currency: string,
): string {
  return lowerFirst(capabilityPhrase(capability, scopeNames, currency));
}

/** "a, b and c" — the join a person would speak, not a bulleted list. */
function joinPhrases(parts: readonly string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/**
 * How many capabilities a sentence can carry before it stops being a sentence. A manager holds
 * every one of the fifty-odd in the catalog, and reading all of them aloud tells nobody anything;
 * the first few plus a count does, and the full list is directly underneath.
 */
const SENTENCE_LIMIT = 5;

/**
 * What somebody's roles let them do, said the way a manager would say it out loud.
 *
 * Built from the resolved capabilities the register itself will be handed, never from the roles the
 * person holds. Those are two different questions once someone holds two roles: the answer to
 * "what can Ana do" is the union with the most permissive cap, and a browser working that out for
 * itself is a second implementation of a rule that has to match the register exactly.
 */
export function capabilitySentence(
  name: string,
  phrases: readonly string[],
  hasRoles: boolean,
  stillEmployed = true,
): string {
  /*
   * A leaver's roles stay on their record, and the endpoint keeps answering with everything those
   * roles allow — so without this the page reads "Yolany can take payment, apply a discount up to
   * L 500…" about somebody who left in March. Two things are wrong with saying that, and only one
   * of them is grammar: nothing takes a role away when employment ends, so the honest sentence is
   * not "she cannot" either. It is that the roles are still sitting there, which is the fact the
   * owner can actually do something about.
   */
  if (!stillEmployed) {
    return hasRoles
      ? `${name} no longer works here. These roles are still on their record — take them away if they should no longer count.`
      : `${name} no longer works here and holds no role.`;
  }
  if (!hasRoles) {
    return `${name} holds no role, so a register will not let them do anything when they sign in.`;
  }
  if (phrases.length === 0) {
    return `${name}'s roles do not allow anything yet. Open a role above to say what it covers.`;
  }
  const shown = phrases.slice(0, SENTENCE_LIMIT);
  const rest = phrases.length - shown.length;
  const tail = rest > 0 ? [`${rest} other ${rest === 1 ? 'thing' : 'things'}`] : [];
  return `${name} can ${joinPhrases([...shown, ...tail])}.`;
}
